/**
 * Equipment — frontend mirror of the NestJS Equipment model
 * (Phase 3a). An Equipment row represents a physical installation
 * on a specific vessel, optionally nested under a parent Equipment
 * (e.g. "Cylinder #3" under "Main Engine PS").
 *
 * Each row has:
 *   - a stable identification tag scoped to its vessel (e.g. "ME-PS")
 *   - a reference to an EquipmentType (the catalog entry)
 *   - a vesselId locating it
 *   - an optional parentEquipmentId for hierarchical sub-components
 *   - a criticality level 1..5 driving WO priority defaults
 */

export const WARRANTY_SOURCES = [
  'INHERITED',
  'ORIGINAL',
  'EXTENDED_BY_REPAIR',
] as const;
export type WarrantySource = (typeof WARRANTY_SOURCES)[number];

export interface Equipment {
  id: string;
  name: string;
  tag: string;
  serialNumber: string | null;

  equipmentTypeId: string;
  vesselId: string;
  parentEquipmentId: string | null;

  location: string | null;

  installedAt: string | null;
  commissionedAt: string | null;
  /** L4 — earliest covered date (typically commissioning). */
  warrantyStartDate: string | null;
  /** L4 — falls back to Vessel.warrantyEndDate when source=INHERITED. */
  warrantyEndDate: string | null;
  /** L4 — INHERITED follows the vessel cascade; ORIGINAL is sticky;
   *  EXTENDED_BY_REPAIR was bumped by a signed WO done while still
   *  under warranty. */
  warrantySource: WarrantySource;

  isActive: boolean;
  criticality: number;
  metadata: Record<string, unknown> | null;

  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

/** Criticality is a 1..5 integer; 1 = not critical, 5 = safety of life. */
export const CRITICALITY_LEVELS = [1, 2, 3, 4, 5] as const;
export type CriticalityLevel = (typeof CRITICALITY_LEVELS)[number];

/** Tailwind palette for the criticality badge. Higher = more alarming. */
export function criticalityColor(level: number): string {
  if (level >= 5) return 'bg-red-100 text-red-700';
  if (level >= 4) return 'bg-orange-100 text-orange-700';
  if (level >= 3) return 'bg-amber-100 text-amber-700';
  if (level >= 2) return 'bg-sky-100 text-sky-700';
  return 'bg-slate-100 text-slate-700';
}
